/**
 * Creates a mutation observer.
 *
 * @param {Object} options - The options object.
 * @param {HTMLElement} options.target - The target element.
 * @param {Function} options.handleMutations - The handle mutations function.
 * @returns {MutationObserver} The mutation observer.
 */
export function createMutationObserver({ target, handleMutations }) {
    const observer = new MutationObserver((mutations) => {
        handleMutations(mutations);
    });

    observer.observe(target, { childList: true, subtree: true });

    return observer;
}

/**
 * Transforms transaction keys by adding a section.table prefix and
 * removing any parenthetical text from labels.
 *
 * Prefix is derived from the numeric item ID:
 *   section = Math.floor(itemId / 1000)  →  1 (Part B), 2 (Part C), 3 (Part D)
 *   table   = Math.floor((itemId % 1000) / 100)  →  1, 2, 3, 4
 *   prefix  = "section.table: "
 *
 * Example: itemId 1201, label "Total Purchase/Acquisition Price (excluding any taxes...)"
 *   → "B.2: Total Purchase/Acquisition Price"
 *
 * @param {Object} transactions - Flat transactions { itemId: { label: value } }
 * @returns {Object} Transformed transactions with prefixed, cleaned keys
 */
export function transformTransactionKeys(transactions) {
    const result = {};
    const sectionPrefix = ["B", "C", "D"];

    Object.entries(transactions).forEach(([itemId, row]) => {
        const numericId = parseInt(itemId, 10);
        const section = Math.floor(numericId / 1000);
        const table = Math.floor((numericId % 1000) / 100);
        const prefix = `${sectionPrefix[section - 1]}.${table}`;

        const transformedRow = {};
        Object.entries(row).forEach(([label, value]) => {
            const cleaned = label.replace(/\s*\([^)]*\)/g, "").trim();
            transformedRow[`${prefix}: ${cleaned}`] = value;
        });

        result[itemId] = transformedRow;
    });

    return result;
}
